import React, { useState, useCallback, useEffect } from 'react';
import SearchBar from '../SearchBar/SearchBar';
// Use VoyagerSearchContext for Voyager API integration
import { useVoyagerSearch as useSearch } from '../../context/VoyagerSearchContext';
import { geocodePlace } from '../../Services/GeocodingService';
import './SearchHeader.scss';

interface SearchFields {
  title: boolean;
  abstract: boolean;
  keyword: boolean;
  any: boolean;
}

interface SearchHeaderProps {
  searchFields: SearchFields;
  activeFilter: string;
  placeholderText: string;
  onCheckboxChange: (field: keyof SearchFields) => void;
  onFilterChange: (filter: string) => void;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchFields,
  activeFilter,
  placeholderText,
  onCheckboxChange,
  onFilterChange,
}) => {
  const { searchState, updateSearchQuery, applySearchParams } = useSearch();

  // Local state for location query and enhanced mode - initialize from context
  const [locationQuery, setLocationQuery] = useState(
    searchState.query?.locationQuery || '',
  );
  const [isEnhanced, setIsEnhanced] = useState(
    searchState.query?.isEnhanced ?? true,
  );
  const [spatialOperator, setSpatialOperator] = useState<
    'within' | 'intersects' | 'contains'
  >(searchState.query?.spatialOperator || 'within');

  // Sync local state with context state when it changes (e.g., from URL params)
  useEffect(() => {
    if (searchState.query?.locationQuery !== undefined) {
      setLocationQuery(searchState.query.locationQuery);
    }
    if (searchState.query?.spatialOperator) {
      setSpatialOperator(searchState.query.spatialOperator);
    }
  }, [searchState.query?.locationQuery, searchState.query?.spatialOperator]);

  const handleSearchChange = (value: string) => {
    updateSearchQuery(value);
  };

  const handleLocationChange = useCallback((value: string) => {
    setLocationQuery(value);
  }, []);

  const handleEnhancedChange = useCallback((value: boolean) => {
    setIsEnhanced(value);
  }, []);

  const handleSpatialOperatorChange = useCallback((value: string) => {
    setSpatialOperator(value as 'within' | 'intersects' | 'contains');
  }, []);

  const handleSearch = async () => {
    // If location query is provided, geocode it to get place info and bbox
    if (locationQuery.trim()) {
      try {
        const geocodeResult = await geocodePlace(locationQuery.trim());
        if (geocodeResult) {
          // Apply search with place info and bbox
          applySearchParams({
            pageIndex: 0,
            place: {
              name: geocodeResult.name,
              id: geocodeResult.id,
              finder: geocodeResult.finder,
              bbox: geocodeResult.bbox,
            },
            spatialOperator,
            locationQuery: locationQuery.trim(),
            bbox: geocodeResult.bboxCorners,
          });
          return;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Geocoding error:', error);
      }
    }

    // No location query or geocoding failed - search without place filter
    applySearchParams({
      pageIndex: 0,
      place: undefined,
      bbox: searchState.query?.spatialFilter?.bbox || [],
      spatialOperator,
      locationQuery: '',
    });
  };

  return (
    <div className="search-header">
      <div className="search-bar-container">
        <SearchBar
          searchQuery={searchState.query?.search || ''}
          onSearchChange={handleSearchChange}
          onSearch={handleSearch}
          placeholderText={placeholderText}
          locationQuery={locationQuery}
          onLocationChange={handleLocationChange}
          isEnhanced={isEnhanced}
          onEnhancedChange={handleEnhancedChange}
          spatialOperator={spatialOperator}
          onSpatialOperatorChange={handleSpatialOperatorChange}
        />
      </div>
    </div>
  );
};

export default SearchHeader;
